import {
    PoseLandmarker,
    FilesetResolver,
    DrawingUtils
} from "@mediapipe/tasks-vision"; // "https://cdn.jsdelivr.net/npm/@mediapipe/tasks-vision@0.10.21";

const LANDMARK_COORDINATE_NAMES = [
    'nose',
    'eye-left-inner',
    'eye-left',
    'eye-left-outer',
    'eye-right-inner',
    'eye-right',
    'eye-right-outer',
    'ear-left',
    'ear-right',
    'mouth-left',
    'mouth-right',
    'shoulder-left',
    'shoulder-right',
    'elbow-left',
    'elbow-right',
    'wrist-left',
    'wrist-right',
    'pinky-left',
    'pinky-right',
    'index-left',
    'index-right',
    'thumb-left',
    'thumb-right',
    'hip-left',
    'hip-right',
    'knee-left',
    'knee-right',
    'ankle-left',
    'ankle-right',
    'heel-left',
    'heel-right',
    'foot-left-index',
    'foot-right-index'
];
export default class GrmPoseLandmarker {
    static LANDMARK_UTILS = {
        bodyParts: {
            head: {min: 0, max: 10},
            hand: {min: 15, max: 22},
            upperBody: {min: 11, max: 22},
            lowerBody: {min: 23, max: 32}
        },
        getLandmarkCoordinateNames: function(){
            return LANDMARK_COORDINATE_NAMES.concat();
        },
        getShownLandmarkIdxList: function(landmarks, size={width, height}){
            const result = [];
            result.rect = {
                total: {
                    minX:undefined, minY:undefined,
                    maxX:undefined, maxY:undefined
                },
                bodyParts: {}
            };
            function calc(rect, landmark){
                let {minX,minY, maxX,maxY} = rect;
                minX = minX === undefined ? landmark.x : Math.min(minX, landmark.x);
                minY = minY === undefined ? landmark.y : Math.min(minY, landmark.y);
                maxX = maxX === undefined ? landmark.x : Math.max(maxX, landmark.x);
                maxY = maxY === undefined ? landmark.y : Math.max(maxY, landmark.y);
                Object.assign(rect, {
                    minX,minY, maxX,maxY
                });
            }
            landmarks.forEach((landmark,i) => {
                if(landmark.x > 1 || landmark.y > 1){
                    return;
                }
                calc(result.rect.total, landmark);

                const bodyPartName = this.getBodyPartNameByCoordiIdx(i);
                result.rect.bodyParts[bodyPartName] = result.rect.bodyParts[bodyPartName] || {
                    minX:undefined, minY:undefined,
                    maxX:undefined, maxY:undefined
                }
                calc(result.rect.bodyParts[bodyPartName], landmark);

                result.push({
                    name: LANDMARK_COORDINATE_NAMES[i],
                    bodyPartName,
                    idx: i,
                    landmark
                });
            });
            function parse(rect){
                const {minX, minY, maxX, maxY} = rect;
                rect = {
                    x: minX, y: minY,
                    width: maxX-minX,
                    height: maxY-minY,
                };
                rect.percent = parseFloat((((rect.width*rect.height) / 1) * 100).toFixed(2));
                if(size.width && size.height){
                    rect.pixel = {
                        x: size.width * rect.x,
                        y: size.height * rect.y,
                        width: size.width * rect.width,
                        height: size.height * rect.height,
                    }
                    rect.pixel.percent = parseFloat(
                                            ((rect.pixel.width * rect.pixel.height
                                                / (size.width * size.height)) * 100).toFixed(2)
                                            );
                }
                return rect;
            }
            result.rect.total = parse(result.rect.total);
            for(let key in result.rect.bodyParts){
                result.rect.bodyParts[key] = parse(result.rect.bodyParts[key]);
            }

            const bParts = Object.keys(result.rect.bodyParts);
            result.wholeBody = ['head', 'upperBody', 'lowerBody'].every(v => bParts.includes(v));
            return result;
        },
        getLandmarkNames: function(idxList){
            const list = idxList.map(idx => LANDMARK_COORDINATE_NAMES[idx]);
            return list;
        },
        getBodyPartNameByCoordiIdx: function(coordiIdx){
            for(let part in this.bodyParts){
                const range = this.bodyParts[part];
                if(coordiIdx >= range.min && coordiIdx <= range.max){
                    return part;
                }
            }
            return null;
        },
        getBodyPartNames: function(idxList){
            let result = [];
            for(let part in this.bodyParts){
                if(this.hasIdx(idxList, this.bodyParts[part])){
                    result.push({
                        name: part,
                        rect: []
                    });
                }
            }
            if(this.hasHands(idxList)){
                result.push('hands');
            }
            return result;
        },
        hasIdx: function(idxList, range){
            // const find = idxList.filter(idx => idx >= range.min && idx <= range.max);
            // return find.length > 0;
            for(let idx of idxList){
                if(idx >= range.min && idx <= range.max){
                    return true;
                }
            }
        },
        hasHead: function(idxList){
            return this.hasIdx(idxList, this.bodyParts.head);
        },
        hasHand: function(idxList){
            return this.hasIdx(idxList, this.bodyParts.hand);
        },
        hasHands: function(idxList){
            let left=false, right=false;
            t:for(let idx of idxList){
                if(!left){
                    switch(idx){
                        case 15:
                        case 17:
                        case 19:
                        case 21:
                            left = true;
                            continue t;
                    }
                }
                if(!right){
                    switch(idx){
                        case 16:
                        case 18:
                        case 20:
                        case 22:
                            right = true;
                            break;
                    }
                }
            }
            return left && right;
        },
        hasUpperBody: function(idxList){
            return this.hasIdx(idxList, this.bodyParts.upperBody);
        },
        hasLowerBody: function(idxList){
            return this.hasIdx(idxList, this.bodyParts.lowerBody);
        }
    };

    options = {};
    poseLandmarker;
    detected = false;
    run = false;

    constructor(options) {
        Object.assign(this.options, options);
    }

    async init () {
        if(this.poseLandmarker){
            return this;
        }

        const options = this.options;
        if(!options.poseLandmarker) options.poseLandmarker = {};
        await (async () => {
            const vision = await FilesetResolver.forVisionTasks(
                options.libs?.urls.visionTasks
                || "https://cdn.jsdelivr.net/npm/@mediapipe/tasks-vision@0.10.21/wasm"
                // || "@mediapipe/tasks-vision/wasm"
            );

            const createOptions = Object.assign({
                runningMode: 'VIDEO',
                numPoses: 1,
                // outputSegmentationMasks: true
            }, options.poseLandmarker, {
                baseOptions: Object.assign({
                    modelAssetPath: 'lite',
                    delegate: 'GPU'
                }, options.poseLandmarker.baseOptions),
                // runningMode: 'IMAGE', // 최초 IMAGE 로 init 해야 IMAGE -> VIDEO toggle 가능 -> 이후 재설정 시 시간이 걸려 VIDEO 사용.
            });

            if(createOptions.baseOptions.modelAssetPath){
                switch(createOptions.baseOptions.modelAssetPath){
                    case 'lite':
                        createOptions.baseOptions.modelAssetPath = 'https://storage.googleapis.com/mediapipe-models/pose_landmarker/pose_landmarker_lite/float16/1/pose_landmarker_lite.task';
                        break;
                    case 'full':
                        createOptions.baseOptions.modelAssetPath = 'https://storage.googleapis.com/mediapipe-models/pose_landmarker/pose_landmarker_full/float16/latest/pose_landmarker_full.task';
                        break;
                }
            }

            this._createOptions = createOptions;
            this.poseLandmarker = await PoseLandmarker.createFromOptions(vision, createOptions);
            // if(options.poseLandmarker.runningMode !== 'IMAGE'){
            //     this.poseLandmarker.setOptions({runningMode: options.poseLandmarker.runningMode || 'VIDEO'})
            // }
            // pl.graphRunner.setAutoResizeCanvas(false);
            // Object.assign(pl.graphRunner.wasmModule.canvas, {
            //     width: 160,
            //     height: video.videoHeight * (video.videoWidth/160)}
            // )
        })();
        return this;
    }

    async setOptions (options) {
        Object.assign(this.options, options);
    }

    async setLandmarkerOptions (options) {
        return this.poseLandmarker.setOptions(options);
    }

    getPoseLandmarker () {
        return this.poseLandmarker;
    }

    async detectForVideo ({
      video, landmarkCanvas, drawLandmark,
      callback, detectIntervalMs,
      debugTime
    }) {
        const {poseLandmarker} = this;

        const cvs = landmarkCanvas;
        let ctx, drawingUtils;
        if(cvs){
            ctx = cvs.getContext('2d');
            drawingUtils = new DrawingUtils(ctx);
        }
        const clearCvs = () => {
            if(ctx && cvs) {
                ctx.clearRect(0, 0, cvs.width, cvs.height);
            }
        }

        const RunData = {
            run: true,
            destroy: false,
            timer: null,
            lastVideoTime: -1,
            detectCount: 0,
            callbackData: {
                changeDetectIntervalMs: (ms) => {
                    if(detectIntervalMs !== ms && video.srcObject){
                        const track = video.srcObject.getVideoTracks()[0];
                        if(track){
                            const frameRate = track.getSettings().frameRate;
                            ms = Math.max(ms, 1000 / frameRate);
                        }
                    }

                    detectIntervalMs = ms;
                    if(RunData.run){
                        if(RunData.runTimer){
                            clearTimeout(RunData.runTimer);
                        }
                        RunData.runTimer = setTimeout(() => {
                            delete RunData.runTimer;
                            globalThis.requestAnimationFrame(predictVideo);
                        }, detectIntervalMs || 1000);
                    }
                },
                setDrawLandmark: (draw) => {
                    drawLandmark = !!draw;
                },
                pause: () => {
                    if(!RunData.run) return;
                    RunData.run = false;
                    clearCvs();
                    clearTimeout(RunData.runTimer);
                },
                resume: () => {
                    if(RunData.destroy){
                        throw 'destroyed';
                    }
                    if(RunData.run) return;
                    RunData.run = true;
                    RunData.lastVideoTime = video.currentTime;
                    predictVideo();
                },
                destroy: function(){
                    this.pause();
                    RunData.destroy = true;
                },
                isDestroyed: function(){
                    return RunData.destroy;
                }
            }
        };
        if(drawLandmark !== false && landmarkCanvas){
            drawLandmark = true;
        }
        const predictVideo = async ()  => {
            if(!RunData.run){
                console.log('# predict video : stopped');
                clearCvs();
                return;
            }
            if(!video.isConnected){
                RunData.callbackData.destroy();
                return;
            }

            let paused = video.paused;
            if (RunData.lastVideoTime !== video.currentTime) {
                if (RunData.lastVideoTime !== video.currentTime) {
                    RunData.lastVideoTime = video.currentTime;
                }
                const sourceSize = video.getBoundingClientRect();
                let st = Date.now();
                const result = await (async () => {
                    const {baseOptions} = poseLandmarker;
                    let streamMode = (baseOptions.getUseStreamMode && baseOptions.getUseStreamMode())
                                    || (baseOptions.g && baseOptions.g())
                                    || (this._createOptions.runningMode === 'VIDEO');
                    if(streamMode){
                        let timestamp = performance.now();
                        return poseLandmarker.detectForVideo(video, timestamp);
                    }else{
                        const bitmap = await createImageBitmap(video);
                        return poseLandmarker.detect(bitmap)
                    }
                })();
                RunData.detectCount++;
                let time = Date.now() - st;

                this.detected = result.landmarks.length > 0;

                if(!RunData.run){
                    console.log('# predict video : stopped 2');
                    clearCvs();
                    return;
                }

                let parseData;
                if(this.detected){
                    parseData = GrmPoseLandmarker.LANDMARK_UTILS.getShownLandmarkIdxList(
                        result.landmarks[0],
                        {width: sourceSize.width, height: sourceSize.height}
                    )
                }
                Object.assign(RunData.callbackData, {
                    video,
                    sourceSize: sourceSize,
                    detected: this.detected,
                    data: result,
                    parseData
                });
                if(debugTime){
                    const debugTime = RunData.callbackData.debug = RunData.callbackData.debug || {
                        totalTime: 0,
                    };
                    debugTime.totalTime += time;
                    Object.assign(debugTime, {
                        lastTime: time,
                        avgTime: parseFloat((debugTime.totalTime / RunData.detectCount).toFixed(2))
                    });
                }
                callback(RunData.callbackData);

                if(drawLandmark && cvs){
                    Object.assign(cvs, {
                        width: `${video.offsetWidth}`,
                        height: `${video.offsetHeight}`
                    });

                    ctx.save();
                    clearCvs();
                    for (const landmark of result.landmarks) {
                        drawingUtils.drawLandmarks(landmark, {
                            radius: (data) => {
                                return DrawingUtils.lerp(data.from && data.from.z, -0.15, 0.1, 0.7, 1);
                            }
                        });
                        drawingUtils.drawConnectors(landmark, PoseLandmarker.POSE_CONNECTIONS, {
                            lineWidth: 1
                        });
                    }
                    ctx.restore();
                }else if(cvs){
                    clearCvs();
                }
            }else if(video.currentTime === 0){
                if(
                    isNaN(video.duration)
                    || !video.srcObject
                    || !video.src
                ){
                    paused = true;
                }
            }

            if(paused){
                video.addEventListener('playing', e => {
                    console.log('# predict video : continue');
                    predictVideo();
                }, {
                    once: true
                })
                console.log('# predict video : paused');
                return;
            }

            // clearTimeout(RunData.runTimer);
            // RunData.runTimer = setTimeout(() => {
            //     window.requestAnimationFrame(predictVideo);
            // }, detectIntervalMs || 1000);
            RunData.callbackData.changeDetectIntervalMs(detectIntervalMs || 1000)
        } // end of predictVideo
        predictVideo();
    }
}
